import { useIsAdmin } from "@/hooks/useIsAdmin";

/**
 * Props for the AdminOnly component
 */
interface AdminOnlyProps {
  /** The content to render when user has appropriate access */
  children: React.ReactNode;
  /** When true, allows both managers and admins access. When false, only admins can access. Defaults to false. */
  requireManager?: boolean;
  /** Optional fallback content to render when user lacks access. If not provided, renders null. */
  fallback?: React.ReactNode;
}

/**
 * AdminOnly wrapper component that provides conditional rendering for admin-only UI elements.
 * 
 * This component uses the useIsAdmin hook to determine user access levels and conditionally
 * renders children based on the user's role. It supports two access modes:
 * - Admin only (default): Only users with 'admin' role can see the content
 * - Manager or Admin: Users with either 'manager' or 'admin' role can see the content
 * 
 * @example
 * // Admin-only content
 * <AdminOnly>
 *   <Button>Delete All Users</Button>
 * </AdminOnly>
 * 
 * @example
 * // Manager or Admin content with fallback
 * <AdminOnly requireManager fallback={<p>Access denied</p>}>
 *   <UserManagementPanel />
 * </AdminOnly>
 */
export function AdminOnly({ 
  children, 
  requireManager = false, 
  fallback = null 
}: AdminOnlyProps) {
  const { isAdmin, isManagerOrAdmin } = useIsAdmin();

  // Determine if user has appropriate access based on requireManager prop
  const hasAccess = requireManager ? isManagerOrAdmin : isAdmin;

  // Return children if user has access, fallback otherwise
  return hasAccess ? <>{children}</> : <>{fallback}</>;
}